import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Snowflake } from 'lucide-react';
import { supabase } from '../../supabaseClient';
import { useAuth } from '../../contexts/AuthContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { getPlantIcon, getIconSrc, buildSeedInsertFromCatalog, normalizeForComparison } from '../../utils';
import QuickAddSeedModal from '../SeedInventory/QuickAddSeedModal';

/**
 * Fields to fetch from catalog_plants for the inline search results.
 */
const CATALOG_SELECT_FIELDS = [
  'id', 'plant', 'variety', 'category', 'season', 'harvest_season',
  'days_to_production', 'preferred_start_method', 'weeks_indoors_before_transplant',
  'spacing', 'frost_tolerant', 'sun_requirement', 'water_needs',
  'direct_sow_weeks_before_last_frost', 'type',
].join(', ');

const SEASON_STYLES = {
  warm: { bg: 'bg-orange-100', text: 'text-orange-700', label: 'Warm' },
  cool: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Cool' },
};

/**
 * A single row in the picker dropdown.
 */
const PickerRow = React.memo(({ record, onSelect }) => {
  const iconSrc = getIconSrc(getPlantIcon(record.plant, record.icon, record.variety));
  const season = SEASON_STYLES[record.season];

  return (
    <button
      type="button"
      onClick={() => onSelect(record)}
      className="w-full px-3 py-2 text-left hover:bg-gray-50 transition-colors flex items-center gap-2"
    >
      <div
        className="flex-shrink-0 flex items-center justify-center rounded bg-[#C7F464]/20 overflow-hidden"
        style={{ width: 22, height: 22 }}
      >
        {iconSrc ? (
          <img src={iconSrc} alt={record.plant} style={{ width: 28, height: 28, maxWidth: 'none' }} />
        ) : (
          <span className="text-xs">🌱</span>
        )}
      </div>
      <span className="flex-1 min-w-0 text-sm text-[#2F5233] truncate">
        <span className="font-medium">{record.plant}</span>
        {record.variety && <span className="text-[#52A675]"> ({record.variety})</span>}
      </span>
      {record.frost_tolerant && (
        <span className="flex-shrink-0 inline-flex items-center gap-0.5 px-1 py-0.5 rounded text-[9px] font-medium bg-cyan-50 text-cyan-700">
          <Snowflake size={8} />Frost
        </span>
      )}
      {season && (
        <span className={`flex-shrink-0 inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-medium ${season.bg} ${season.text}`}>
          {season.label}
        </span>
      )}
    </button>
  );
});

/**
 * CatalogAwarePlantPicker
 *
 * Inline autocomplete dropdown that searches both the user's inventory (My Plants)
 * and the plant catalog (From Catalog) simultaneously. Used on Plantings, Harvests,
 * and Tasks add forms.
 *
 * - My Plants section always shown first (instant, local filter)
 * - From Catalog section shown when filterValue has content (debounced Supabase search)
 * - "Can't find your variety? Add it custom →" always pinned at the bottom
 * - Selecting a catalog plant auto-adds it to inventory (with dedup check) and
 *   broadcasts a seeds-updated event so other pages stay in sync
 *
 * @param {Array}    props.seeds         - User's current seed inventory
 * @param {string}   props.filterValue   - Current text input value (used to filter/search)
 * @param {Function} props.onSelect      - Called with (plant, variety, seedRecord) on selection
 * @param {Function} props.onClose       - Called to close the dropdown
 * @param {Function} [props.onSeedAdded] - Called with newSeedRecord after catalog insert
 * @param {string}   [props.maxHeight]   - Tailwind max-height class (default: 'max-h-56')
 */
const CatalogAwarePlantPicker = ({
  seeds = [],
  filterValue = '',
  onSelect,
  onClose,
  onSeedAdded,
  maxHeight = 'max-h-56',
}) => {
  const { user } = useAuth();
  const { isPro, getLimits } = useSubscription();
  const limits = getLimits();

  const [catalogResults, setCatalogResults] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [showQuickAdd, setShowQuickAdd] = useState(false);

  const debouncedRef = useRef(null);
  const trimmed = filterValue.trim().toLowerCase();

  // Real inventory seeds only — excludes synthetic layout entries (_fromLayout) that some
  // callers (e.g. AddPlantingForm) mix in for display but are not owned by the user.
  const inventorySeeds = useMemo(() => seeds.filter(s => !s?._fromLayout), [seeds]);

  // O(1) dedup lookups (based on real inventory only)
  const addedCatalogIds = useMemo(
    () => new Set(inventorySeeds.filter(s => s.catalog_id).map(s => s.catalog_id)),
    [inventorySeeds]
  );

  const seedsByNameVariety = useMemo(() => {
    const map = new Map();
    inventorySeeds.forEach(s => {
      const key = normalizeForComparison(s.plant) + '|' + normalizeForComparison(s.variety || '');
      if (!map.has(key)) map.set(key, s);
    });
    return map;
  }, [inventorySeeds]);

  // Inventory matches — instant, local
  const inventoryResults = useMemo(() => {
    if (!trimmed) {
      return [...seeds].sort((a, b) => {
        const p = a.plant.toLowerCase().localeCompare(b.plant.toLowerCase());
        if (p !== 0) return p;
        return (a.variety || '').toLowerCase().localeCompare((b.variety || '').toLowerCase());
      });
    }
    return seeds
      .filter(s =>
        s.plant.toLowerCase().includes(trimmed) ||
        (s.variety || '').toLowerCase().includes(trimmed)
      )
      .sort((a, b) => {
        const p = a.plant.toLowerCase().localeCompare(b.plant.toLowerCase());
        if (p !== 0) return p;
        return (a.variety || '').toLowerCase().localeCompare((b.variety || '').toLowerCase());
      });
  }, [seeds, trimmed]);

  // Catalog results — deduplicated against inventory
  const deduplicatedCatalog = useMemo(() => {
    return catalogResults.filter(record => {
      if (addedCatalogIds.has(record.id)) return false;
      const key = normalizeForComparison(record.plant) + '|' + normalizeForComparison(record.variety || '');
      return !seedsByNameVariety.has(key);
    });
  }, [catalogResults, addedCatalogIds, seedsByNameVariety]);

  // Debounced catalog search — only fires when there's a search term
  useEffect(() => {
    if (!trimmed) {
      setCatalogResults([]);
      setCatalogLoading(false);
      return;
    }

    let cancelled = false;

    setCatalogLoading(true);
    clearTimeout(debouncedRef.current);
    debouncedRef.current = setTimeout(async () => {
      const { data } = await supabase
        .from('catalog_plants')
        .select(CATALOG_SELECT_FIELDS)
        .or(`plant.ilike.%${trimmed}%,variety.ilike.%${trimmed}%`)
        .order('plant', { ascending: true })
        .order('variety', { ascending: true })
        .limit(30);
      if (cancelled) return;
      setCatalogResults(data || []);
      setCatalogLoading(false);
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(debouncedRef.current);
    };
  }, [trimmed]);

  const handleInventorySelect = (seed) => {
    onSelect(seed.plant, seed.variety || '', seed);
    onClose();
  };

  const handleCatalogSelect = async (record) => {
    if (adding) return;

    // Check by catalog_id
    if (addedCatalogIds.has(record.id)) {
      const existing = seeds.find(s => s.catalog_id === record.id);
      onSelect(record.plant, record.variety || '', existing);
      onClose();
      return;
    }

    // Check by normalized name+variety
    const nameKey = normalizeForComparison(record.plant) + '|' + normalizeForComparison(record.variety || '');
    if (seedsByNameVariety.has(nameKey)) {
      const existing = seedsByNameVariety.get(nameKey);
      onSelect(record.plant, record.variety || '', existing);
      onClose();
      return;
    }

    // Not in inventory — insert
    if (!user?.id) {
      alert('Please sign in to add plants.');
      onClose();
      return;
    }

    if (!isPro && inventorySeeds.length >= limits.seeds) {
      alert(`You've reached your free seed limit (${limits.seeds}). Upgrade to Pro to add more plants.`);
      onClose();
      return;
    }

    setAdding(true);
    try {
      const seedData = buildSeedInsertFromCatalog(user.id, record);
      const { data, error } = await supabase
        .from('seeds')
        .insert(seedData)
        .select()
        .single();
      if (error || !data) {
        alert("Couldn't add plant — try again.");
        return;
      }
      // Broadcast so all pages stay in sync (inventory seeds only — excludes layout suggestions)
      window.dispatchEvent(new CustomEvent('seeds-updated', { detail: { seeds: [...inventorySeeds, data] } }));
      if (onSeedAdded) onSeedAdded(data);
      onSelect(data.plant, data.variety || '', data);
      onClose();
    } finally {
      setAdding(false);
    }
  };

  const showMyPlants = inventoryResults.length > 0;
  const isGrouped = showMyPlants && trimmed.length > 0;

  // Nothing to show — still render so the footer link is always visible
  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-40" onClick={onClose} />

      <div className={`absolute top-full left-0 right-0 mt-1 bg-white border-2 border-gray-200 rounded-lg shadow-lg z-50 ${maxHeight} overflow-y-auto flex flex-col`}>
        <div className="flex-1 overflow-y-auto">
          {/* My Plants section */}
          {showMyPlants && (
            <>
              {isGrouped && (
                <div className="px-3 py-1.5 bg-gray-100 text-xs font-semibold text-gray-500 uppercase tracking-wide sticky top-0">
                  My Plants
                </div>
              )}
              {inventoryResults.map(seed => (
                <PickerRow key={seed.id} record={seed} onSelect={handleInventorySelect} />
              ))}
            </>
          )}

          {/* From Catalog section */}
          {trimmed.length > 0 && (
            <>
              {isGrouped && (
                <div className="px-3 py-1.5 bg-gray-100 text-xs font-semibold text-gray-500 uppercase tracking-wide sticky top-0">
                  From Catalog
                </div>
              )}
              {catalogLoading ? (
                <div className="px-3 py-2 text-xs text-gray-400">Searching catalog…</div>
              ) : adding ? (
                <div className="px-3 py-2 text-xs text-[#52A675] font-medium">Adding to My Plants…</div>
              ) : deduplicatedCatalog.length > 0 ? (
                deduplicatedCatalog.map(record => (
                  <PickerRow key={record.id} record={record} onSelect={handleCatalogSelect} />
                ))
              ) : (
                <div className="px-3 py-2 text-xs text-gray-400 italic">No catalog matches for &ldquo;{filterValue}&rdquo;</div>
              )}
            </>
          )}

          {/* Prompt to search catalog when no term entered */}
          {!trimmed && !showMyPlants && (
            <div className="px-3 py-2 text-xs text-gray-400 italic">Start typing to search your plants and the catalog</div>
          )}

          {/* Hint to search catalog when only showing inventory */}
          {!trimmed && showMyPlants && (
            <div className="px-3 py-1.5 text-xs text-gray-400 text-center border-t border-gray-100">
              Type to also search the plant catalog
            </div>
          )}

          {/* No results at all */}
          {trimmed && !showMyPlants && !catalogLoading && deduplicatedCatalog.length === 0 && (
            <div className="px-3 py-2 text-xs text-gray-400 italic">No matches found</div>
          )}
        </div>

        {/* Footer — always visible */}
        <div className="border-t border-gray-100 sticky bottom-0 bg-white">
          <button
            type="button"
            onClick={() => { setShowQuickAdd(true); }}
            className="w-full px-3 py-2 text-left text-xs text-[#52A675] hover:text-[#4A9168] hover:bg-[#52A675]/5 font-medium transition-colors"
          >
            Can&apos;t find your variety? Add it custom →
          </button>
        </div>
      </div>

      <QuickAddSeedModal
        isOpen={showQuickAdd}
        onClose={() => setShowQuickAdd(false)}
        onSave={(newSeed) => {
          // Broadcast inventory seeds only — excludes layout suggestions
          window.dispatchEvent(new CustomEvent('seeds-updated', { detail: { seeds: [...inventorySeeds, newSeed] } }));
          if (onSeedAdded) onSeedAdded(newSeed);
          onSelect(newSeed.plant, newSeed.variety || '', newSeed);
          setShowQuickAdd(false);
          onClose();
        }}
        isPro={isPro}
        seedLimit={limits.seeds}
        currentSeedCount={inventorySeeds.length}
      />
    </>
  );
};

export default CatalogAwarePlantPicker;
